import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema, ReportIdSchema } from "../primitives/ids.js";
import { ReportLifecycleStatusSchema } from "./report-lifecycle.js";
import { EXTERNAL_READINESS_RULE_SET_VERSION } from "./report-lifecycle.js";
import { PublicEvidenceClassSchema } from "../growth/evidence-class.js";
export const ExternalReadinessCategorySchema = z.enum([
    "businessClarity",
    "serviceClarity",
    "localRelevance",
    "contactLeadPath",
    "trustAndProof",
    "claimRisk",
    "contentQuality",
    "schemaSearchReadinessBasics",
    "handoffOwnershipGaps",
]);
export const ExternalReadinessSeveritySchema = z.enum(["LOW", "MEDIUM", "HIGH"]);
export const ExternalReadinessPrioritySchema = z.enum(["P1", "P2", "P3", "P4", "P5"]);
export const ExternalReadinessEvidenceKindSchema = z.enum([
    "SCAN",
    "IMPORT",
    "CONFIRMED_BG",
    "INFERRED",
]);
export const ExternalReadinessFindingV1Schema = z
    .object({
    schemaId: z.literal("externalReadiness.finding.v1"),
    findingId: z.string().min(1),
    category: ExternalReadinessCategorySchema,
    severity: ExternalReadinessSeveritySchema,
    priority: ExternalReadinessPrioritySchema,
    title: z.string().min(1).max(200),
    recommendation: z.string().min(1).max(2000),
    sourceRef: z.string().min(1).max(2048),
    evidenceKind: ExternalReadinessEvidenceKindSchema,
    needsProof: z.boolean(),
    clientSafeSummary: z.string().min(1).max(500),
})
    .strict();
export const ExternalReadinessSourceRefsV1Schema = z
    .object({
    schemaId: z.literal("externalReadiness.sourceRefs.v1"),
    externalScanSnapshotIds: z.array(z.string().min(1)),
    importDraftIds: z.array(z.string().min(1)),
    appliedDraftId: z.string().min(1).optional(),
    businessGraphDraftId: z.string().min(1).optional(),
    attachedUrl: z.string().url().optional(),
})
    .strict();
/** Canonical internal storage document for Phase 4 external readiness. */
export const ExternalReadinessReportV1Schema = z
    .object({
    schemaVersion: z.literal("external-readiness-report.v1.0"),
    reportId: ReportIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    reportType: z.literal("EXTERNAL_READINESS"),
    lifecycleStatus: ReportLifecycleStatusSchema,
    ruleSetVersion: z.literal(EXTERNAL_READINESS_RULE_SET_VERSION),
    sourceRefs: ExternalReadinessSourceRefsV1Schema,
    headline: z.string().min(1),
    categories: z.array(ExternalReadinessCategorySchema),
    findings: z.array(ExternalReadinessFindingV1Schema),
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    generatedAt: z.string().datetime(),
})
    .strict();
export const ExternalReadinessInternalViewV1Schema = z
    .object({
    schemaId: z.literal("externalReadiness.internalView.v1"),
    reportId: ReportIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    lifecycleStatus: ReportLifecycleStatusSchema,
    headline: z.string().min(1),
    sourceRefs: ExternalReadinessSourceRefsV1Schema,
    categories: z.array(ExternalReadinessCategorySchema),
    findings: z.array(ExternalReadinessFindingV1Schema),
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    generatedAt: z.string().datetime(),
    clientSafe: z.literal(false),
})
    .strict();
export const ExternalReadinessClientSafeFindingV1Schema = z
    .object({
    categoryLabel: z.string().min(1),
    severity: ExternalReadinessSeveritySchema,
    summary: z.string().min(1),
    recommendation: z.string().min(1),
    evidenceClass: PublicEvidenceClassSchema,
})
    .strict();
export const ExternalReadinessClientSafeSummaryV1Schema = z
    .object({
    schemaId: z.literal("externalReadiness.clientSafeSummary.v1"),
    reportId: ReportIdSchema,
    projectId: ProjectIdSchema,
    lifecycleStatus: z.enum(["READY_CLIENT_SAFE"]),
    headline: z.string().min(1),
    findings: z.array(ExternalReadinessClientSafeFindingV1Schema),
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    generatedAt: z.string().datetime(),
    clientSafe: z.literal(true),
})
    .strict();
export const GenerateExternalReadinessRequestV1Schema = z
    .object({
    schemaId: z.literal("externalReadiness.generateRequest.v1"),
    forceRegenerate: z.boolean().optional(),
})
    .strict();
export const GenerateExternalReadinessResultV1Schema = z
    .object({
    schemaId: z.literal("externalReadiness.generateResult.v1"),
    reportId: ReportIdSchema,
    lifecycleStatus: ReportLifecycleStatusSchema,
    findingCount: z.number().int().min(0),
    generatedAt: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=external-readiness-report.js.map